'use client'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface MultiDropzoneProps {
  accept: string
  helpText: string
  /** Append semantics — the caller owns the queue, this component only reports newly picked
   * files. Mirrors `<Dropzone>`'s single-file `onFileChange` but for a `FileList`/multi-select. */
  onFilesAdded: (files: File[]) => void
}

/**
 * Multi-file sibling of `Dropzone` (`./dropzone.tsx`) — kept as a separate component rather than
 * adding a `multiple` prop to the existing one, since `Dropzone` is shared with the onboarding
 * funnel's single-document step and changing its contract there isn't worth the risk for a feature
 * only `UploadTokenView` needs.
 */
export function MultiDropzone({ accept, helpText, onFilesAdded }: MultiDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed p-5 text-center transition-colors',
        dragOver ? 'border-primary bg-primary/5' : 'border-border',
      )}
      role="button"
      tabIndex={0}
      aria-label="Upload documents"
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const dropped = Array.from(e.dataTransfer.files ?? [])
        if (dropped.length > 0) onFilesAdded(dropped)
      }}
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-muted-foreground"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
      <span className="text-foreground text-sm font-medium">Drop your files here</span>
      <span className="text-muted-foreground text-sm">
        or{' '}
        <span className="text-primary underline-offset-4 hover:underline">
          click to browse files
        </span>
      </span>
      <span className="text-muted-foreground text-xs">{helpText}</span>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple
        hidden
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? [])
          if (picked.length > 0) onFilesAdded(picked)
          // Without this, picking the same file(s) again after removing them from the queue
          // wouldn't re-fire onChange (the input's value never changed).
          e.target.value = ''
        }}
      />
    </div>
  )
}
